import StatisticsReportCard, {
  StatisticsReportCardSkeleton,
} from "@/admin/dashboard/components/StatisticsReportCard"
import {
  QuizSubmissionsOverviewContentHeaderQuery,
  QuizSubmissionsOverviewContentHeaderQuery$variables,
} from "@/content-usage/drawer/quizzes/review-submissions/__generated__/QuizSubmissionsOverviewContentHeaderQuery.graphql"
import { QuizSubmissionsOverviewContentHeader_ContentUsageFragment$key } from "@/content-usage/drawer/quizzes/review-submissions/__generated__/QuizSubmissionsOverviewContentHeader_ContentUsageFragment.graphql"
import { ContentType } from "@/content-usage/drawer/__generated__/InlineContentDrawerTemplateFragment.graphql"
import { useLabel } from "@/core/context/LabelsContext"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import { WebFormSubmissionsUtils } from "@/web-form/utils/webFormSubmissionsUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { formatElapsedTime } from "@utils/time/timeUtils"
import { TestIDProps } from "@utils/typeUtils"
import { useFragment, useSubscribeToInvalidationState } from "react-relay"
import { graphql } from "relay-runtime"

interface QuizSubmissionsOverviewContentHeaderProps extends TestIDProps {
  contentLabel: string
  contentUsageId: GlobalID
  webFormId: GlobalID
  contentType: ContentType
  contentUsageKey?: QuizSubmissionsOverviewContentHeader_ContentUsageFragment$key | null
}

function QuizSubmissionsOverviewContentHeader({
  testid,
  contentLabel,
  contentUsageId,
  webFormId,
  contentType,
  contentUsageKey = null,
}: QuizSubmissionsOverviewContentHeaderProps) {
  const isSurvey = contentType === "survey"

  const contentUsage =
    useFragment<QuizSubmissionsOverviewContentHeader_ContentUsageFragment$key>(
      graphql`
        fragment QuizSubmissionsOverviewContentHeader_ContentUsageFragment on ContentUsage
        @argumentDefinitions(roles: { type: "[ProductRole!]" }) {
          passPercentage
          product {
            productMemberships(roles: $roles) {
              totalCount
            }
            adminCanLearnMode
          }
        }
      `,
      contentUsageKey
    )

  const queryVars: QuizSubmissionsOverviewContentHeaderQuery$variables = {
    webFormId,
    contentUsageId,
    includePercentage: !isSurvey,
    membersOnly: contentUsage?.product ? !contentUsage.product.adminCanLearnMode : false,
  }
  const [{ webForm }, refetch] =
    Relay.useRefetchableQuery<QuizSubmissionsOverviewContentHeaderQuery>(
      graphql`
        query QuizSubmissionsOverviewContentHeaderQuery(
          $webFormId: ID!
          $contentUsageId: ID!
          $includePercentage: Boolean!
          $membersOnly: Boolean
        ) {
          webForm: node(id: $webFormId) {
            ... on WebForm {
              submissionsAverageDurationSeconds(
                contentUsageId: $contentUsageId
                membersOnly: $membersOnly
              )
              submissionsAveragePercentage(
                contentUsageId: $contentUsageId
                membersOnly: $membersOnly
              ) @include(if: $includePercentage)
            }
          }
        }
      `,
      queryVars,
      { fetchPolicy: "network-only" }
    )

  useSubscribeToInvalidationState([webFormId], () => refetch(queryVars))
  const isPassFail = typeof contentUsage?.passPercentage === "number"
  const totalMembers = contentUsage?.product?.productMemberships?.totalCount ?? 0
  const averageDuration = webForm?.submissionsAverageDurationSeconds ?? 0
  const averagePercentage = webForm?.submissionsAveragePercentage ?? 0

  const incomplete = WebFormSubmissionsUtils.useCount({
    contentUsageId,
    webFormId,
    completed: false,
    include: !isPassFail,
    membersOnly: contentUsage?.product ? !contentUsage.product.adminCanLearnMode : false,
  })

  const completed = WebFormSubmissionsUtils.useCount({
    contentUsageId,
    webFormId,
    completed: true,
    membersOnly: contentUsage?.product ? !contentUsage.product.adminCanLearnMode : false,
  })

  const passed = WebFormSubmissionsUtils.useCount({
    contentUsageId,
    webFormId,
    completed: true,
    passed: true,
    uniqueByMember: true,
    include: isPassFail,
    membersOnly: contentUsage?.product ? !contentUsage.product.adminCanLearnMode : false,
  })

  const total = incomplete + completed
  const classes = useStyles()
  const memberLabel = useLabel("admin_member")

  return (
    <div className={classes.header} data-testid={`${testid}.header`}>
      {isPassFail ? (
        <StatisticsReportCard
          testid={`${testid}.passed-submissions`}
          value={`${passed}/${totalMembers}`}
          subtitle={`${memberLabel.plural} Passed`}
          tooltipText={`The total number of ${memberLabel.plural} who have a passed ${contentLabel}.`}
          classes={{ root: classes.card }}
        />
      ) : (
        <StatisticsReportCard
          testid={`${testid}.total-submissions`}
          value={`${completed}/${total}`}
          subtitle={"Submissions"}
          tooltipText={`The total number of completed submissions for this ${contentLabel}.`}
          classes={{ root: classes.card }}
        />
      )}

      {isSurvey ? (
        <StatisticsReportCard
          testid={`${testid}.completion-rate`}
          value={total ? `${Math.round((completed / total) * 100)}%` : "--"}
          subtitle={"Completion Rate"}
          tooltipText={`The percentage of ${memberLabel.plural} who completed this ${contentLabel} after starting it.`}
          classes={{ root: classes.card }}
        />
      ) : (
        <StatisticsReportCard
          testid={`${testid}.average-score`}
          value={completed ? `${averagePercentage}%` : "--"}
          subtitle={"Average Score"}
          tooltipText={`The average score of completed submissions for this ${contentLabel}.`}
          classes={{ root: classes.card }}
        />
      )}

      <StatisticsReportCard
        testid={`${testid}.average-duration`}
        value={completed ? formatElapsedTime(averageDuration, { precision: 2 }) : "--"}
        subtitle={"Average Time Spent"}
        tooltipText={`The average time taken to complete this ${contentLabel}.`}
        classes={{ root: classes.card }}
      />
    </div>
  )
}

const useStyles = makeUseStyles((theme) => ({
  header: {
    display: "flex",
    width: "100%",
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gridTemplateColumns: "1fr",
    },
  },
  card: {
    width: "100%",
    boxShadow: theme.palette.groovyDepths.insideCard,
  },
}))

export function QuizSubmissionsOverviewContentHeaderSkeleton() {
  const classes = useStyles()
  return (
    <div className={classes.header}>
      <StatisticsReportCardSkeleton classes={{ root: classes.card }} />
      <StatisticsReportCardSkeleton classes={{ root: classes.card }} />
      <StatisticsReportCardSkeleton classes={{ root: classes.card }} />
    </div>
  )
}

export default Relay.withSkeleton({
  skeleton: QuizSubmissionsOverviewContentHeaderSkeleton,
  component: QuizSubmissionsOverviewContentHeader,
})
